import { action, set } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { AjaxError } from 'ember-ajax/errors';
import { isUnauthorizedError } from 'ember-ajax/errors';
import type { Featurescontext, Hotel, Ota, User } from 'frontend-utils/models';
import type { UserLocale } from 'frontend-utils/models/user';
import { HOTEL_COOKIE_NAME } from 'frontend-utils/utils/constants/constant-values';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import ENV from 'lumen/config/environment';
import type { Transition } from 'lumen/services/transition';

export default class ApplicationRoute extends Route {
  @service declare alerting: Services['alerting'];
  @service declare cookie: Services['cookie'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare intlLoader: Services['intl-loader'];
  @service declare locale: Services['locale'];
  @service declare router: Services['router'];
  @service declare satismeter: Services['satismeter'];
  @service declare store: Services['store'];
  @service declare theme: Services['theme'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare userEngagement: Services['user-engagement'];

  defaultDestinationSubscriptionId: string | null | undefined = null;
  defaultHotelId: string | null | undefined = null;

  async afterModel(model: User, transition: Transition): Promise<void> {
    // If the user doesn't have access to the hotel in the cookie the aferModel hook is fired, but the model is empty
    // Do this check to avoid error dialog popping up
    if (!model) {
      transition.abort();
      return;
    }

    // Quick fix for https://otainsight.atlassian.net/browse/FE-1382
    // Stop the `afterModel` from executing multiple times, similar to the `model`.
    if (this.hotelAndMarketService.user) {
      return;
    }

    set(this.hotelAndMarketService, 'user', model);
    const userId = model.id;

    // @ts-expect-error: Type 'string | null' is not assignable to type 'string'.
    window.oi_userId = userId;

    // This should normally be using "this.access.isStaffUser", but when using the service in the application route
    // the whole app errors on permissions. We suspect this has something to do with computed properties not updating
    // so we're manually getting the context for now
    const featureContext =
      this.store.peekAll<Featurescontext>('featurescontext')[0];
    const isStaff =
      featureContext?.availableFeatures.indexOf('user.staff') !== -1;

    const adminLink = `${ENV.adminUserURL}${userId}`;

    // @ts-expect-error: Type 'string | null' is not assignable to type 'string | number | undefined'.
    this.errorTracking.setUser({ id: userId, isStaff, adminLink });
    this.errorTracking.setContext('culture', { locale: model.locale });

    await this.intlLoader.load(model.locale);
    this.locale.setLocale(model.locale);

    this.alerting.setTranslations({
      showErrorAlert: {
        title: this.intl.t('general.error_title'),
        text: this.intl.t('general.error'),
      },
      showConfirmWarningAlert: {
        title: this.intl.t('general.are_you_sure'),
        buttons: {
          cancel: {
            text: this.intl.t('general.no'),
          },
          confirm: {
            text: this.intl.t('general.yes'),
          },
        },
      },
    });

    this.usageAnalytics.initialise();

    this.satismeter.initialise();

    await this.userEngagement.getSeenUserEngagementElements();

    this.theme.start();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    if (this.hotelAndMarketService.user) {
      return this.hotelAndMarketService.user;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const cookie = this.cookie.getCookie(HOTEL_COOKIE_NAME);

    if (cookie) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const hotelId = cookie.id;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { type } = cookie;

      if (type === 'hotel' && hotelId) {
        const queries = Promise.all([
          // Prefetch hotel if we have the last viewed hotel cookie
          this.store.query<User>('user', {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            hotel_id: hotelId,
            only_self: true,
          }),
          this.store.query<Ota>('ota', {}),
        ]);

        return queries.then(
          ([success]) => success[0],
          () => {
            // Reset this cookie and retry if we no longer have access to this hotel
            this.cookie.resetCookie(HOTEL_COOKIE_NAME);
            this.redirectToRoot();
          },
        );
      }
    }

    return Promise.allSettled([
      this.store.query<User>('user', {
        only_self: true,
      }),
      this.store.query<Ota>('ota', {}),
    ]).then(([userResult, otaResult]) => {
      // If user query failed, prioritize that error
      if (userResult.status === 'rejected') {
        throw userResult.reason;
      }

      // If user succeeded but OTA failed, throw OTA error
      if (otaResult.status === 'rejected') {
        throw otaResult.reason;
      }

      const users = userResult.value;
      const meta = users.meta as unknown as
        | {
            default_destination_subscription_id: string;

            default_hotel_id: string;
          }
        | undefined;

      set(this, 'defaultHotelId', meta?.default_hotel_id);
      set(
        this,
        'defaultDestinationSubscriptionId',
        meta?.default_destination_subscription_id,
      );
      return users[0];
    });
  }

  redirect(model: User, transition: Transition): void {
    if (transition.to.name !== 'index') {
      return;
    }

    // Try falling back to last hotel from cookie first, redirect to hotel user has access to
    const hotelCookie = this.cookie.getCookie(HOTEL_COOKIE_NAME) as {
      id: string;
      otaId?: string;
      type: 'hotel' | 'market';
    } | null;

    switch (hotelCookie?.type) {
      case 'hotel': {
        if (hotelCookie?.otaId) {
          this.router.transitionTo('hotel', hotelCookie.id, {
            queryParams: {
              otaId: hotelCookie.otaId,
            },
          });
        } else {
          this.router.transitionTo('hotel', hotelCookie.id);
        }

        break;
      }

      case 'market': {
        if (hotelCookie?.otaId) {
          this.router.transitionTo('market', hotelCookie.id, {
            queryParams: {
              otaId: hotelCookie.otaId,
            },
          });
        } else {
          this.router.transitionTo('market', hotelCookie.id);
        }

        break;
      }

      default: {
        const hotelId =
          this.defaultHotelId ?? this.store.peekAll<Hotel>('hotel')[0]?.id;

        if (!hotelId) {
          const destinationId = this.defaultDestinationSubscriptionId;
          this.router.transitionTo('destination', destinationId);
        } else {
          this.router.transitionTo('hotel', hotelId);
        }
      }
    }
  }

  redirectToLogin(): void {
    const nextUrlAfterLogin = encodeURIComponent(
      `${window.location.pathname}${window.location.search}`,
    );

    const host = getHostDomain({ useDevApi: ENV.useDevApi });
    window.location.replace(`${host}/login/?next=${nextUrlAfterLogin}`);
  }

  redirectToRoot(): void {
    window.location.replace('/');
  }

  /**
   * Needed because doRefresh is bubbled up in rates and ranking controller, when the route is not neccesarily active.
   * This will stop the propagation and prevent an error.
   */
  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  doRefresh() {
    return false;
  }

  @action error(error: unknown): void {
    if (isUnauthorizedError(error as AjaxError)) {
      this.redirectToLogin();
      return;
    }

    this.errorTracking.captureException(error);
  }
}

declare global {
  interface Window {
    locale: UserLocale;

    oi_userId: string;
  }
}
